import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';
import * as EmailValidator from 'email-validator';
import { DDD } from '../constants/objects';

import { smilesMaskEmail, smilesMaskPhone, formatMask } from './mask';
import { isValidBirthDate, isValidDate } from './date';

type ValidType = 'NAME' | 'EMAIL' | 'CPF' | 'MEMBER' | undefined;

const isValidCpf = (
  documentCPF: string,
): {
  isValid: boolean;
  type: ValidType;
} => {
  const cpf = documentCPF.replace(/\.|-/g, '');
  if (!cpf || cpf.length < 11) {
    return {
      isValid: false,
      type: undefined,
    };
  }

  if (
    cpf.length !== 11 ||
    cpf === '00000000000' ||
    cpf === '11111111111' ||
    cpf === '22222222222' ||
    cpf === '33333333333' ||
    cpf === '44444444444' ||
    cpf === '55555555555' ||
    cpf === '66666666666' ||
    cpf === '77777777777' ||
    cpf === '88888888888' ||
    cpf === '99999999999'
  ) {
    return {
      isValid: false,
      type: 'CPF',
    };
  }
  let sum = 0;
  let rest;
  for (let i = 1; i <= 9; i += 1) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (11 - i);
  }
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) {
    rest = 0;
  }

  if (rest !== parseInt(cpf.substring(9, 10), 10)) {
    return {
      isValid: false,
      type: 'CPF',
    };
  }
  sum = 0;
  for (let i = 1; i <= 10; i += 1) {
    sum += parseInt(cpf.substring(i - 1, i), 10) * (12 - i);
  }
  rest = (sum * 10) % 11;

  if (rest === 10 || rest === 11) {
    rest = 0;
  }

  if (rest !== parseInt(cpf.substring(10, 11), 10)) {
    return {
      isValid: false,
      type: 'CPF',
    };
  }
  return {
    isValid: true,
    type: 'CPF',
  };
};

const isValidEmail = (
  email: string,
): {
  isValid: boolean;
  type: ValidType;
} => {
  if (email.length >= 3 && email.includes('@')) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()\\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    const isValid =
      re.test(String(email).toLowerCase()) && EmailValidator.validate(email);
    return {
      isValid,
      type: 'EMAIL',
    };
  } else {
    return {
      isValid: false,
      type: undefined,
    };
  }
};

const isValidMemberNumber = (
  memberNumber: string,
): {
  isValid: boolean;
  type: ValidType;
} => {
  if (String(memberNumber).length < 9) {
    return {
      isValid: false,
      type: undefined,
    };
  }

  const re = /^\d+$/;
  const isOnlyNumbers = re.test(memberNumber);
  const isValid = String(memberNumber).length === 9 && isOnlyNumbers;
  return {
    isValid,
    type: 'MEMBER',
  };
};

type validMemberCpfTypes = {
  isValid: boolean;
  type: ValidType;
  mask?: string;
};
const isValidMemberNumberOrCPF = (str: string): validMemberCpfTypes => {
  const value = str.replace(/\.|-/g, '');

  if (
    String(value).length <= 8 ||
    String(value).length === 10 ||
    String(value).length >= 12
  ) {
    return {
      isValid: false,
      type: undefined,
    };
  } else if (String(value).length === 9) {
    return isValidMemberNumber(value);
  } else {
    const isValidCPF = isValidCpf(value);
    return isValidCPF.isValid
      ? {
          ...isValidCPF,
          mask: formatMask(value, '000.000.000-00'),
        }
      : { ...isValidCPF };
  }
};

const isValidPassportOrRNE = (document: string): boolean => {
  const re = /^([0-9a-zA-Z ]){1,20}$/g;
  return re.test(String(document).trim());
};

type validMemberNameTypes = {
  isValid: boolean;
  type: ValidType;
  name?: string;
};
const isValidName = (
  name: string,
  singleName?: boolean,
): validMemberNameTypes => {
  const aName = name.trim().split(' ');
  const normalized = aName.reduce(
    (finalName: string, partName: string) =>
      finalName + (partName ? ' ' : '') + partName,
  );

  if (singleName) {
    if (
      aName.length < 1 &&
      (normalized.length < 2 || normalized.length > 100)
    ) {
      return {
        isValid: false,
        type: undefined,
        name: normalized,
      };
    }
  } else {
    if (aName.length < 2 && normalized.length < 6) {
      return {
        isValid: false,
        type: undefined,
        name: normalized,
      };
    }
  }

  if (normalized.includes('--') || normalized.includes('´´')) {
    return {
      isValid: false,
      type: 'NAME',
      name: normalized,
    };
  }

  const re = /^([a-zÇçA-ZáàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ\-´ ]){2,100}$/g;
  return {
    isValid: re.test(normalized),
    type: 'NAME',
    name: normalized,
  };
};

type validPasswordParamsTypes = {
  password: string;
  birthDate?: string;
};
const isValidPassword = (params: validPasswordParamsTypes): boolean => {
  const { password, birthDate } = params;
  const aPass = password.trim().split('');
  if (aPass.length < 4) {
    return false;
  }
  const aSeqAsc = [];
  const aSeqDesc = [];
  const aEquals = [];
  for (let i = 0; i < aPass.length; i++) {
    const valueAsc = Number(aPass[0]) + i;
    const valueDesc = Number(aPass[0]) - i;
    aSeqAsc.push(valueAsc > 9 ? valueAsc.toString()[1] : valueAsc.toString());
    aSeqDesc.push(
      valueDesc > 9 ? valueDesc.toString()[1] : valueDesc.toString(),
    );
    aEquals.push(aPass[0]);
  }

  if (
    JSON.stringify(aSeqAsc) === JSON.stringify(aPass) || // sequencia - ex: 1234, 9012
    JSON.stringify(aSeqDesc) === JSON.stringify(aPass) || // sequencia - ex: 8901, 4321
    JSON.stringify(aEquals) === JSON.stringify(aPass) // repetidos - ex: 1111, 8888
  ) {
    return false;
  }

  if (birthDate) {
    const normalized = birthDate.replace(new RegExp('/', 'g'), '');

    if (
      password.search(normalized.substring(0, 4)) > -1 || // dia + mes
      password.search(normalized.substring(4)) > -1 // ano
    ) {
      return false;
    }
  }
  return true;
};

type validPhoneNumber = {
  isValid: boolean;
  phoneNumber?: string;
  nationalNumber?: string;
  URI?: string;
  DDI?: {
    value?: string;
    formatInternational?: string;
    country?: CountryCode;
  };
  DDD?: {
    isValid: boolean;
    value?: string;
    formatInternational?: string;
  };
  error?: string;
};

const isValidPhoneNumber = (phoneNumber: string): validPhoneNumber => {
  try {
    const finalPhoneNumber = !phoneNumber.includes('+')
      ? `+${phoneNumber}`
      : phoneNumber;

    const phoneNumberParsed = parsePhoneNumber(finalPhoneNumber);
    let valueDDD;
    let isValidDDD = false;
    if (phoneNumberParsed) {
      const regExp = /\(([^)]+)\)/;
      const matches = regExp.exec(phoneNumberParsed.formatNational());
      valueDDD = matches?.[1];
      if (valueDDD) {
        isValidDDD =
          phoneNumberParsed?.country === 'BR'
            ? DDD[valueDDD] !== undefined
            : true;
      }
    }

    const finalPhone =
      `+${phoneNumberParsed?.countryCallingCode?.toString()} ` +
      phoneNumberParsed
        ?.formatNational()
        .substr(phoneNumberParsed?.formatNational().indexOf('('));

    let isValidPhoneNumber = phoneNumberParsed?.isValid();
    if (!isValidPhoneNumber) {
      isValidDDD = false;
    } else if (isValidPhoneNumber && phoneNumberParsed?.country === 'BR') {
      isValidPhoneNumber =
        phoneNumberParsed?.number?.toString().length === 14 &&
        new RegExp(
          /^(?:\+)[0-9]{2}\s?(?:\()[0-9]{2}(?:\))\s?[9]{1}\s?[0-9]{3,4}(?:-)[0-9]{4}$/,
        ).test(finalPhone);
    }
    return {
      isValid: isValidPhoneNumber || false,
      phoneNumber: phoneNumberParsed?.number?.toString(),
      nationalNumber: phoneNumberParsed?.formatNational(),
      URI: phoneNumberParsed?.getURI(),
      DDI: {
        value: phoneNumberParsed?.countryCallingCode?.toString(),
        formatInternational: phoneNumberParsed?.formatInternational(),
        country: phoneNumberParsed?.country,
      },
      DDD: {
        isValid: isValidDDD,
        value: valueDDD,
        formatInternational: finalPhone,
      },
    };
  } catch (error) {
    return {
      isValid: false,
      error: error.message,
    };
  }
};

type Country = {
  iso3: string;
  country: string;
  countryToSave: string;
  ddi: string;
  iso2: string;
};

type AddressType = {
  country: Country;
  zipCode: string;
  street: string;
  complement: string;
  number: string;
  neigborhood: string;
  state: string;
  city: string;
};

type validAddress = {
  isValid: boolean;
  country?: string;
  countryToSave?: string;
  zipCodeNumber?: number;
  zipCode?: string;
  street?: string;
  complement?: string;
  number?: string;
  neigborhood?: string;
  state?: string;
  city?: string;
  message?: string;
};
const isValidAddress = (address: AddressType): validAddress => {
  let responseZipCode;
  if (address.country.iso3 == 'BRA') {
    responseZipCode = isValidZipCode(address.zipCode);
  } else {
    responseZipCode = isValidPostalCode(address.zipCode);
  }
  if (!responseZipCode.isValid) {
    return {
      isValid: false,
      message: responseZipCode.message,
    };
  }
  let responseValidSize;
  responseValidSize = isValidAddressSize(address.country.country, 2, 50, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  responseValidSize = isValidAddressSize(address.street, 2, 50, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  responseValidSize = isValidAddressSize(address.complement, 0, 20, false);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  responseValidSize = isValidAddressSize(address.number, 1, 10, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  if (isNaN(Number(address.number))) {
    return {
      isValid: false,
      message: 'O número deve conter apenas números.',
    };
  }
  responseValidSize = isValidAddressSize(address.neigborhood, 2, 50, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  responseValidSize = isValidAddressSize(address.state, 2, 50, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  responseValidSize = isValidAddressSize(address.city, 2, 50, true);
  if (!responseValidSize.isValid) {
    return {
      isValid: false,
      message: responseValidSize.message,
    };
  }
  return {
    isValid: true,
    country: address.country.country,
    countryToSave: address.country.countryToSave,
    zipCodeNumber: responseZipCode.zipCodeNumber,
    zipCode: responseZipCode.zipCode,
    street: address.street,
    complement: address.complement,
    number: address.number,
    neigborhood: address.neigborhood,
    state: address.state,
    city: address.city,
  };
};

type validZipCode = {
  isValid: boolean;
  zipCodeNumber?: number;
  zipCode?: string;
  message?: string;
};
const isValidZipCode = (zipCode: string): validZipCode => {
  const zipCodeParsed = zipCode.substring(0, 5) + zipCode.substring(6);
  if (isNaN(Number(zipCodeParsed))) {
    return {
      isValid: false,
      message: 'CEP deve conter apenas números.',
    };
  }
  const zipCodeParsedNumber = Number(zipCodeParsed);
  if (zipCodeParsed.length != 8) {
    return {
      isValid: false,
      message: 'CEP deve conter 8 dígitos.',
    };
  }
  return {
    isValid: true,
    zipCodeNumber: zipCodeParsedNumber,
    zipCode: zipCode,
  };
};

type validPostalCode = {
  isValid: boolean;
  zipCodeNumber?: number;
  zipCode?: string;
  message?: string;
};
const isValidPostalCode = (postalCode: string): validPostalCode => {
  if (isNaN(Number(postalCode))) {
    return {
      isValid: false,
      message: 'Código postal deve conter apenas números.',
    };
  }
  if (postalCode.length > 15) {
    return {
      isValid: false,
      message: 'Código postal deve conter 15 dígitos.',
    };
  }

  return {
    isValid: true,
    zipCodeNumber: Number(postalCode),
    zipCode: postalCode,
  };
};

type validAddressSize = {
  isValid: boolean;
  message?: string;
};
const isValidAddressSize = (
  address: string,
  min: number,
  max: number,
  required: boolean,
): validAddressSize => {
  if (required && address == '') {
    return {
      isValid: false,
      message: 'Campo obrigatório não preenchido.',
    };
  }
  if (address.length < min || address.length > max) {
    return {
      isValid: false,
      message: `O número de caracteres deve ser entre ${min} e ${max}.`,
    };
  }
  return {
    isValid: true,
  };
};

const isValidLogin = (
  value: string,
): {
  isValid: boolean;
  type: ValidType;
  mask?: string;
} => {
  const isOnlyLetters = /^[A-Za-zÇçáàâãéèêíïóôõöúçñÁÀÂÃÉÈÍÏÓÔÕÖÚÇÑ.\-+._#@$]+$/.test(
    value,
  );
  if (!value.includes('@') && !isOnlyLetters) {
    const responseValid = isValidMemberNumberOrCPF(value);
    if (responseValid.isValid || responseValid.type) return responseValid;
  }
  return isValidEmail(value);
};

type validName = {
  firstName: string;
  lastName: string;
};

const formatName = (name: string): validName => {
  const fullName = name.split(' ');
  return {
    firstName: fullName[0],
    lastName: fullName.splice(1).join(','),
  };
};

export {
  isValidCpf,
  isValidEmail,
  isValidMemberNumber,
  isValidMemberNumberOrCPF,
  isValidPassportOrRNE,
  isValidName,
  isValidBirthDate,
  isValidPassword,
  isValidPhoneNumber,
  isValidAddress,
  isValidDate,
  isValidLogin,
  formatName,
  formatMask,
  smilesMaskEmail,
  smilesMaskPhone,
};
